import {
  type ExpandedState,
  type OnChangeFn,
  type SortingState,
  getCoreRowModel,
  getExpandedRowModel,
} from "@tanstack/react-table"
import type { FC, RefObject } from "react"

import { useCustomerOrderColumns } from "../../hooks/useCustomerOrderColumns"

import { CustomerOrderRowDetails } from "./CustomerOrderRowDetails"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { expandedCellStyles } from "@/components/Tables/Aeros/shared"
import type {
  CustomerLocationCombination,
  CustomerOrderDateRangeType,
  SalesOrderStatus,
} from "@/graphql/codegen/graphql"

interface CustomerOrderTableProps {
  data: CustomerLocationCombination[]
  loading?: boolean
  error?: Error
  sorting: SortingState
  expanded: ExpandedState
  query?: string
  statuses?: SalesOrderStatus[]
  dateFrom?: string
  dateTo?: string
  dateRangeType?: CustomerOrderDateRangeType
  containerRef?: RefObject<HTMLDivElement>
  onSortingChange: OnChangeFn<SortingState>
  onExpandedChange: OnChangeFn<ExpandedState>
  onScroll?: () => void
  onStartOrder: (row: CustomerLocationCombination) => void
  canStartOrder: boolean
}

export const CustomerOrderTable: FC<CustomerOrderTableProps> = ({
  data,
  error,
  loading = false,
  sorting,
  expanded,
  query,
  statuses,
  dateFrom,
  dateTo,
  dateRangeType,
  containerRef,
  onSortingChange,
  onExpandedChange,
  onScroll,
  onStartOrder,
  canStartOrder,
}) => {
  const columns = useCustomerOrderColumns({ onStartOrder, canStartOrder })

  return (
    <AerosBaseTable<CustomerLocationCombination>
      error={error}
      features={{
        table: { stickyHeader: true },
        tanstackOptions: {
          data,
          columns,
          state: { sorting, isLoading: loading, expanded },
          manualSorting: true,
          enableSorting: true,
          enableMultiSort: false,
          enableSortingRemoval: false,
          getCoreRowModel: getCoreRowModel(),
          getRowId: (row) => row.combinationId,
          onSortingChange,
          onExpandedChange,
          getRowCanExpand: () => true,
          getExpandedRowModel: getExpandedRowModel(),
          renderExpandedContent: (row) => (
            <CustomerOrderRowDetails
              row={row}
              query={query}
              statuses={statuses}
              dateFrom={dateFrom}
              dateTo={dateTo}
              dateRangeType={dateRangeType}
            />
          ),
        },
      }}
      slotProps={{
        container: { ref: containerRef, onScroll },
        bodyRow: { className: "hover:bg-primary-100" },
        bodyCellExpanded: { className: "bg-primary-100" },
        expandedCell: expandedCellStyles,
      }}
      noDataMessage='No customer orders found'
    />
  )
}
